import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// Reusable base query params for paginated + sortable endpoints
export const paginatedQuerySchema = z.object({
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(50),
  sortBy: z.string().optional(),
  sortDirection: z.enum(['asc', 'desc']).optional().default('desc'),
});

const paginatedResponse = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    totalCount: z.number(),
  });

const failedTranscriptionItemSchema = z.object({
  transcriptId: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  appointmentId: z.string(),
  appointmentName: z.string(),
  userId: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  fileName: z.string(),
  fileId: z.string(),
  transcriptType: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const missingNotesItemSchema = z.object({
  appointmentId: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  appointmentName: z.string(),
  userId: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  status: z.string(),
  failureType: z.string().nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
  startDate: z.string().nullable(),
  completedAt: z.string().nullable(),
});

const notesGeneratingItemSchema = z.object({
  appointmentId: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  appointmentName: z.string(),
  userId: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  jobId: z.string(),
  jobStatus: z.string(),
  llmOutputType: z.string(),
  promptTemplateName: z.string().nullable(),
  createdAt: z.string(),
  jobCreatedAt: z.string(),
  durationSeconds: z.number(),
});

const graphileJobItemSchema = z.object({
  id: z.string(),
  taskId: z.number(),
  taskIdentifier: z.string(),
  status: z.enum(['available', 'locked', 'failed', 'scheduled']),
  attempts: z.number(),
  maxAttempts: z.number(),
  payload: z.unknown(),
  priority: z.number(),
  jobQueueId: z.number().nullable(),
  createdAt: z.string(),
  runAt: z.string(),
  updatedAt: z.string(),
  lastError: z.string().nullable(),
  lockedAt: z.string().nullable(),
  lockedBy: z.string().nullable(),
  key: z.string().nullable(),
});

const recentOutputItemSchema = z.object({
  llmOutputId: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  appointmentId: z.string(),
  appointmentName: z.string(),
  userId: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  status: z.string(),
  failureType: z.string().nullable(),
  source: z.string(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  promptTemplateName: z.string().nullable(),
  outputType: z.string().nullable(),
  outputTemplateName: z.string().nullable(),
  modelArgsName: z.string().nullable(),
  modelType: z.string().nullable(),
});

export const adminOnCallContract = c.router(
  {
    getFailedTranscriptions: {
      method: 'GET',
      path: '/failed-transcriptions',
      query: paginatedQuerySchema,
      responses: {
        200: paginatedResponse(failedTranscriptionItemSchema),
      },
      summary: 'Get all failed transcriptions globally',
    },
    getMissingNotes: {
      method: 'GET',
      path: '/missing-notes',
      // maxAgeDays bounds the completed_at scan window. Defaults to 7 so
      // omitting it (including from older clients) stays bounded and fast.
      // Pass 0 for the explicit "All time" view (unbounded, deliberately slow).
      query: paginatedQuerySchema.extend({
        maxAgeDays: z.preprocess(
          (value) => (value === '' ? undefined : value),
          z.coerce.number().int().finite().min(0).optional().default(7),
        ),
      }),
      responses: {
        200: paginatedResponse(missingNotesItemSchema),
      },
      summary:
        'Get all completed appointments missing LLM output notes globally',
    },
    getNotesGenerating: {
      method: 'GET',
      path: '/notes-generating',
      query: paginatedQuerySchema.extend({
        outputType: z.string().optional(),
      }),
      responses: {
        200: paginatedResponse(notesGeneratingItemSchema),
      },
      summary: 'Get all appointments with notes currently generating',
    },
    getRecentOutputs: {
      method: 'GET',
      path: '/recent-outputs',
      query: paginatedQuerySchema.extend({
        outputType: z.string().optional(),
        status: z.string().optional(),
        maxAgeDays: z.coerce.number().optional().default(1),
      }),
      responses: {
        200: paginatedResponse(recentOutputItemSchema),
      },
      summary: 'Get recent LLM outputs with template and model info',
    },
    getGraphileJobs: {
      method: 'GET',
      path: '/graphile-jobs',
      query: paginatedQuerySchema.extend({
        taskIdentifier: z.string().optional(),
        status: z
          .enum(['available', 'locked', 'failed', 'scheduled', 'all'])
          .optional(),
      }),
      responses: {
        200: paginatedResponse(graphileJobItemSchema),
      },
      summary: 'List graphile worker jobs',
    },
    permanentlyFailGraphileJob: {
      method: 'POST',
      path: '/graphile-jobs/:jobId/permanently-fail',
      pathParams: z.object({ jobId: z.string().regex(/^\d+$/) }),
      body: z.object({ reason: z.string().min(1) }),
      responses: {
        200: z.object({ success: z.boolean() }),
        404: z.object({ message: z.string() }),
        422: z.object({ message: z.string() }),
      },
      summary: 'Permanently fail a graphile worker job',
    },
    completeGraphileJob: {
      method: 'POST',
      path: '/graphile-jobs/:jobId/complete',
      pathParams: z.object({ jobId: z.string().regex(/^\d+$/) }),
      body: z.object({}),
      responses: {
        200: z.object({ success: z.boolean() }),
        404: z.object({ message: z.string() }),
        422: z.object({ message: z.string() }),
      },
      summary: 'Complete (remove) a graphile worker job',
    },
    rescheduleGraphileJob: {
      method: 'POST',
      path: '/graphile-jobs/:jobId/reschedule',
      pathParams: z.object({ jobId: z.string().regex(/^\d+$/) }),
      body: z.object({
        runAt: z.string().optional(),
        attempts: z.coerce.number().optional().default(0),
        maxAttempts: z.coerce.number().optional(),
      }),
      responses: {
        200: z.object({ success: z.boolean() }),
        404: z.object({ message: z.string() }),
        422: z.object({ message: z.string() }),
      },
      summary: 'Reschedule a graphile worker job (resets attempts)',
    },
  },
  { pathPrefix: '/admin-on-call' },
);

export type FailedTranscriptionItem = z.infer<
  typeof failedTranscriptionItemSchema
>;
export type MissingNotesItem = z.infer<typeof missingNotesItemSchema>;
export type NotesGeneratingItem = z.infer<typeof notesGeneratingItemSchema>;
export type RecentOutputItem = z.infer<typeof recentOutputItemSchema>;
export type GraphileJobItem = z.infer<typeof graphileJobItemSchema>;
